/**
 * Dictado por voz con la API del navegador (gratis, sin claves). No aparece si el navegador no
 * la ofrece. Escribe en el campo y nunca envía solo: el texto se revisa antes.
 * Privacidad: en Chrome el audio lo procesa Google; en Safari puede procesarse en el dispositivo.
 */
import { useEffect, useRef, useState } from 'react';
import { Mic, MicOff } from 'lucide-react';

interface RecognitionResult {
  readonly 0: { transcript: string };
  readonly isFinal: boolean;
}
interface RecognitionEvent {
  readonly resultIndex: number;
  readonly results: ArrayLike<RecognitionResult>;
}
interface Recognition {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  onresult: ((e: RecognitionEvent) => void) | null;
  onend: (() => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  start(): void;
  stop(): void;
}
type RecognitionCtor = new () => Recognition;

export function speechRecognition(): RecognitionCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as { SpeechRecognition?: RecognitionCtor; webkitSpeechRecognition?: RecognitionCtor };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export function DictationButton({
  lang,
  base,
  onText,
  labels,
  disabled,
}: {
  lang: string;
  /** Texto que ya había en el campo: lo dictado se añade detrás. */
  base: string;
  onText: (text: string) => void;
  labels: { start: string; stop: string; listening: string; privacy: string; error: string };
  disabled?: boolean;
}) {
  const Ctor = speechRecognition();
  const rec = useRef<Recognition | null>(null);
  const [listening, setListening] = useState(false);
  const [error, setError] = useState(false);
  useEffect(() => () => rec.current?.stop(), []);
  if (!Ctor) return null;

  const start = () => {
    setError(false);
    const r = new Ctor();
    r.lang = lang;
    r.interimResults = true;
    r.continuous = false;
    const prefix = base.trim() ? `${base.trim()} ` : '';
    r.onresult = (e) => {
      let said = '';
      for (let i = 0; i < e.results.length; i++) said += e.results[i]![0].transcript;
      onText(`${prefix}${said}`.slice(0, 500));
    };
    r.onerror = (e) => {
      if (e.error !== 'aborted' && e.error !== 'no-speech') setError(true);
    };
    r.onend = () => setListening(false);
    rec.current = r;
    r.start();
    setListening(true);
  };

  return (
    <>
      <button
        type="button"
        className={`btn chat__mic${listening ? ' chat__mic--on' : ''}`}
        aria-pressed={listening}
        aria-label={listening ? labels.stop : labels.start}
        title={labels.privacy}
        disabled={disabled}
        onClick={() => (listening ? rec.current?.stop() : start())}
      >
        {listening ? <MicOff size={18} aria-hidden="true" /> : <Mic size={18} aria-hidden="true" />}
      </button>
      {(listening || error) && (
        <span className="chat__mic-status" role="status">
          {error ? labels.error : `${labels.listening} ${labels.privacy}`}
        </span>
      )}
    </>
  );
}
